/**
 * 도트 아트용 문자 그리드 유틸리티.
 *
 * 모든 기본 스프라이트는 이 유틸로 "1픽셀 = 문자 1개" 그리드를 만들고,
 * 팔레트(문자 → 색)를 적용해 캔버스 텍스처로 굽는다.
 * '.' 은 투명 픽셀이다.
 */

export type Grid = string[][];
export type Palette = Record<string, string>;

export const EMPTY = '.';

export function blank(w: number, h: number): Grid {
  return Array.from({ length: h }, () => Array.from({ length: w }, () => EMPTY));
}

export function size(g: Grid): { w: number; h: number } {
  return { w: g[0]?.length ?? 0, h: g.length };
}

export function clone(g: Grid): Grid {
  return g.map((row) => row.slice());
}

export function px(g: Grid, x: number, y: number, ch: string): void {
  const ix = Math.round(x);
  const iy = Math.round(y);
  if (iy < 0 || iy >= g.length) return;
  if (ix < 0 || ix >= g[iy].length) return;
  g[iy][ix] = ch;
}

export function rect(g: Grid, x: number, y: number, w: number, h: number, ch: string): void {
  for (let iy = y; iy < y + h; iy++) {
    for (let ix = x; ix < x + w; ix++) px(g, ix, iy, ch);
  }
}

/** 채워진 타원. cx/cy 는 중심, rx/ry 는 반지름. */
export function ellipse(g: Grid, cx: number, cy: number, rx: number, ry: number, ch: string): void {
  if (rx <= 0 || ry <= 0) return;
  for (let iy = Math.floor(cy - ry); iy <= Math.ceil(cy + ry); iy++) {
    for (let ix = Math.floor(cx - rx); ix <= Math.ceil(cx + rx); ix++) {
      const dx = (ix - cx) / rx;
      const dy = (iy - cy) / ry;
      if (dx * dx + dy * dy <= 1.02) px(g, ix, iy, ch);
    }
  }
}

export function roundRect(
  g: Grid,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
  ch: string,
): void {
  rect(g, x + r, y, w - r * 2, h, ch);
  rect(g, x, y + r, w, h - r * 2, ch);
  ellipse(g, x + r, y + r, r, r, ch);
  ellipse(g, x + w - 1 - r, y + r, r, r, ch);
  ellipse(g, x + r, y + h - 1 - r, r, r, ch);
  ellipse(g, x + w - 1 - r, y + h - 1 - r, r, r, ch);
}

export function line(g: Grid, x0: number, y0: number, x1: number, y1: number, ch: string): void {
  const steps = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0));
  if (steps === 0) {
    px(g, x0, y0, ch);
    return;
  }
  for (let i = 0; i <= steps; i++) {
    px(g, x0 + ((x1 - x0) * i) / steps, y0 + ((y1 - y0) * i) / steps, ch);
  }
}

/** 채워진 픽셀 바깥쪽 4방향에 외곽선 문자를 두른다. 원작 특유의 굵은 테두리 느낌용. */
export function outline(g: Grid, ch = 'o', over: string = EMPTY): Grid {
  const { w, h } = size(g);
  const out = clone(g);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (g[y][x] !== over) continue;
      const neighbours = [
        g[y - 1]?.[x],
        g[y + 1]?.[x],
        g[y]?.[x - 1],
        g[y]?.[x + 1],
      ];
      if (neighbours.some((n) => n !== undefined && n !== EMPTY && n !== ch)) out[y][x] = ch;
    }
  }
  return out;
}

/** 특정 문자를 다른 문자로 치환 (팔레트 스왑용) */
export function recolor(g: Grid, map: Record<string, string>): Grid {
  return g.map((row) => row.map((c) => map[c] ?? c));
}

export function flipH(g: Grid): Grid {
  return g.map((row) => row.slice().reverse());
}

/** patch 를 (dx, dy) 위치에 덮어 그린다. 투명 픽셀은 무시. */
export function stamp(g: Grid, patch: Grid, dx: number, dy: number): void {
  patch.forEach((row, y) => {
    row.forEach((ch, x) => {
      if (ch === EMPTY) return;
      px(g, x + dx, y + dy, ch);
    });
  });
}

export function fromStrings(rows: readonly string[]): Grid {
  const w = Math.max(...rows.map((r) => r.length));
  return rows.map((r) => r.padEnd(w, EMPTY).split(''));
}

/** 세로로 amount 픽셀 눌린 프레임을 만든다 (몬스터 통통 튀는 모션용) */
export function squash(g: Grid, amount = 1): Grid {
  const { w, h } = size(g);
  const out = blank(w, h);
  const keep = h - amount;
  for (let y = 0; y < keep; y++) {
    const src = Math.min(h - 1, Math.round((y * h) / keep));
    for (let x = 0; x < w; x++) out[y + amount][x] = g[src][x];
  }
  return out;
}

/** 픽셀 단위로 n배 확대 (도트 유지) */
export function scaleGrid(g: Grid, n: number): Grid {
  const out: Grid = [];
  for (const row of g) {
    const scaled: string[] = [];
    for (const ch of row) {
      for (let i = 0; i < n; i++) scaled.push(ch);
    }
    for (let i = 0; i < n; i++) out.push(scaled.slice());
  }
  return out;
}

/** 그리드를 지정 크기 캔버스 안에서 (가로 중앙, 세로 아래) 기준으로 정렬한다. */
export function alignBottomCenter(g: Grid, w: number, h: number): Grid {
  const out = blank(w, h);
  const s = size(g);
  stamp(out, g, Math.floor((w - s.w) / 2), h - s.h);
  return out;
}

export function renderGrid(
  ctx: CanvasRenderingContext2D,
  g: Grid,
  palette: Palette,
  scale: number,
  ox = 0,
  oy = 0,
): void {
  for (let y = 0; y < g.length; y++) {
    for (let x = 0; x < g[y].length; x++) {
      const ch = g[y][x];
      if (ch === EMPTY) continue;
      const color = palette[ch];
      if (!color) continue;
      ctx.fillStyle = color;
      ctx.fillRect(ox + x * scale, oy + y * scale, scale, scale);
    }
  }
}
